// Vano: a trading bot you pay for (in-game WUSD, billed daily). It reads hidden token fates
// with a tier-dependent accuracy: launch calls, rug radar on your bags, and (Whale) auto-snipes.
// The edge is real but never perfect, and every call is graded so you can see if it's worth it.

import { TUNABLES } from "@degen/shared/config/tunables";
import { money } from "@degen/shared/money";
import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { balanceOf } from "../market/token";

const V = TUNABLES.vano;

export type VanoTier = "lite" | "pro" | "whale";
export type VanoCall = "runner" | "mid" | "rug";

export interface VanoSignal {
  id: number;
  tick: number;
  tokenId: string;
  ticker: string;
  kind: "call" | "rug_alert";
  call: VanoCall;
  confidence: number; // shown to the player (0-1)
  outcome?: "hit" | "miss";
  sniped?: number; // WUSD auto-bought
}

export interface VanoState {
  tier: VanoTier | null;
  paidThroughDay: number; // subscription covers through this day
  snipeWusd: number; // Whale auto-snipe size (0 = off)
  signals: VanoSignal[];
  nextId: number;
  hits: number;
  misses: number;
  spent: number;
  warned: Record<string, true>; // tokens already rug-alerted
  called?: Record<string, true>; // tokens Vano has already called (one call per coin)
  scanDue?: boolean; // run a scan of live coins on the next tick (set on subscribe)
}

export function createVano(): VanoState {
  return { tier: null, paidThroughDay: 0, snipeWusd: 0, signals: [], nextId: 1, hits: 0, misses: 0, spent: 0, warned: {} };
}

export const tierDef = (t: VanoTier) => V.tiers[t];

type Result = { ok: true; data?: Record<string, number> } | { ok: false; error: string };

export function subscribe(state: GameState, tier: VanoTier): Result {
  const def = V.tiers[tier];
  if (!def) return { ok: false, error: "Unknown plan." };
  const v = state.vano;
  const p = state.player;
  const day = dayOf(state.tick);
  if (v.tier === tier && v.paidThroughDay >= day) return { ok: false, error: `You're already on ${def.name}.` };
  if (p.cash < def.dailyWusd) return { ok: false, error: `${def.name} is ${money(def.dailyWusd)}/day. Not enough WUSD.` };
  p.cash -= def.dailyWusd;
  v.spent += def.dailyWusd;
  v.tier = tier;
  v.paidThroughDay = day;
  v.scanDue = true; // first look at everything already trading
  if (!def.autoSnipe) v.snipeWusd = 0;
  pushLog(state, "vano", `🤖 ${def.name} activated. ${money(def.dailyWusd)}/day, billed every morning. Cancel any time.`);
  return { ok: true };
}

export function cancel(state: GameState): Result {
  if (!state.vano.tier) return { ok: false, error: "No active plan." };
  pushLog(state, "vano", `🤖 ${V.tiers[state.vano.tier].name} cancelled. Trading on vibes again.`);
  state.vano.tier = null;
  state.vano.snipeWusd = 0;
  return { ok: true };
}

export function setSnipe(state: GameState, wusd: number): Result {
  const v = state.vano;
  if (!v.tier || !V.tiers[v.tier].autoSnipe) return { ok: false, error: "Auto-snipe is a Vano Whale feature." };
  if (!(wusd >= 0) || wusd > V.snipeMaxWusd) return { ok: false, error: `Snipe size: ${money(0)}-${money(V.snipeMaxWusd)}.` };
  v.snipeWusd = Math.round(wusd);
  return { ok: true };
}

/** Morning billing. Can't pay? The bot stops. */
export function vanoNewDay(state: GameState, day: number) {
  const v = state.vano;
  if (!v.tier) return;
  const def = V.tiers[v.tier];
  if (state.player.cash >= def.dailyWusd) {
    state.player.cash -= def.dailyWusd;
    v.spent += def.dailyWusd;
    v.paidThroughDay = day;
  } else {
    pushLog(state, "vano", `🤖 ${def.name} paused: couldn't cover today's ${money(def.dailyWusd)}. Resubscribe in the Vano app.`);
    v.tier = null;
    v.snipeWusd = 0;
  }
}

function callFor(rng: Rng, accuracy: number, fate: string): { call: VanoCall; confidence: number } {
  const roll = rng.next();
  const wrong = rng.chance(0.5);
  const noise = rng.range(-0.06, 0.06);
  const real = truth(fate);
  const others = (["runner", "mid", "rug"] as VanoCall[]).filter((c) => c !== real);
  return { call: roll < accuracy ? real : others[wrong ? 0 : 1], confidence: Math.max(0.5, Math.min(0.97, accuracy + noise)) };
}

const truth = (fate: string): VanoCall => (fate === "runner" ? "runner" : fate === "rug" ? "rug" : "mid");

function addSignal(state: GameState, s: Omit<VanoSignal, "id">) {
  const v = state.vano;
  const sig = { ...s, id: v.nextId++ };
  v.signals.push(sig);
  if (v.signals.length > V.keepSignals) v.signals.splice(0, v.signals.length - V.keepSignals);
  return sig;
}

/**
 * Per tick. Returns tokens to auto-snipe (the engine buys them so gas and holdings work as usual).
 * Always consumes the same RNG draws whether or not you're subscribed, so subscribing never
 * changes how the market itself plays out.
 */
export function vanoTick(state: GameState, rng: Rng): { tokenId: string; wusd: number }[] {
  const v = state.vano;
  const tick = state.tick;
  const def = v.tier ? V.tiers[v.tier] : null;
  const snipes: { tokenId: string; wusd: number }[] = [];
  for (const t of Object.values(state.market.tokens)) {
    if (t.bornTick !== tick || !t.creator.startsWith("npcdev:")) continue;
    const roll = rng.next();
    const wrong = rng.chance(0.5);
    const noise = rng.range(-0.06, 0.06);
    if (!def) continue;
    const real = truth(t.fate);
    const others = (["runner", "mid", "rug"] as VanoCall[]).filter((c) => c !== real);
    const call = roll < def.accuracy ? real : others[wrong ? 0 : 1];
    (v.called ??= {})[t.id] = true;
    const sig = addSignal(state, { tick, tokenId: t.id, ticker: t.ticker, kind: "call", call, confidence: Math.max(0.5, Math.min(0.97, def.accuracy + noise)) });
    if (call === "runner" && def.autoSnipe && v.snipeWusd > 0) {
      snipes.push({ tokenId: t.id, wusd: v.snipeWusd });
      sig.sniped = v.snipeWusd;
    }
  }
  // Scan coins already trading (on subscribe, then hourly): one call per coin, so the feed is never empty.
  if (def && (v.scanDue || tick % 60 === 0)) {
    v.scanDue = false;
    const called = (v.called ??= {});
    for (const t of Object.values(state.market.tokens)) {
      if (called[t.id] || t.phase === "dead" || t.rugged || !t.creator.startsWith("npcdev:")) continue;
      called[t.id] = true;
      const c = callFor(rng, def.accuracy, t.fate);
      addSignal(state, { tick, tokenId: t.id, ticker: t.ticker, kind: "call", ...c });
    }
  }
  // Rug radar on coins you hold.
  if (tick % 10 === 0) {
    for (const h of state.player.holdings) {
      const t = state.market.tokens[h.tokenId];
      if (!t || t.rugged || t.phase === "dead" || balanceOf(t, "player") <= 0 || v.warned[t.id]) continue;
      const realRug = t.fate === "rug" && t.rugTick !== undefined && t.rugTick - tick <= (def?.rugRadarTicks ?? 0) && t.rugTick > tick;
      const roll = rng.next();
      if (!def || def.rugRadarTicks === 0) continue;
      const fire = realRug ? roll < def.accuracy : roll < (V.falseAlarmPerHour / 6) * (1 - def.accuracy);
      if (!fire) continue;
      v.warned[t.id] = true;
      addSignal(state, { tick, tokenId: t.id, ticker: t.ticker, kind: "rug_alert", call: "rug", confidence: def.accuracy });
      pushLog(state, "vano_alert", `🚨 Vano rug radar: $${t.ticker} dev wallet is moving. Consider getting out.`, { tokenId: t.id });
    }
  }
  // Grade calls once they've had time to play out.
  for (const s of v.signals) {
    if (s.outcome || tick - s.tick < V.judgeAfterTicks) continue;
    const t = state.market.tokens[s.tokenId];
    if (!t) continue;
    s.outcome = (s.kind === "rug_alert" ? !!t.rugged : truth(t.fate) === s.call) ? "hit" : "miss";
    if (s.outcome === "hit") v.hits++;
    else v.misses++;
  }
  return snipes;
}

export function vanoAccuracy(v: VanoState): number | null {
  const n = v.hits + v.misses;
  return n ? v.hits / n : null;
}
